import React, { useState } from "react";

const isLocalTutorialHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const TUTORIAL_VIDEO_BASE_URL = isLocalTutorialHost
  ? "https://www.fbxchange.com/ReactPHP/tutorial_files"
  : "/ReactPHP/tutorial_files";
const getTutorialVideoUrl = (fileName) => `${TUTORIAL_VIDEO_BASE_URL}/${fileName}`;
const createOpenBoardVideo = getTutorialVideoUrl("01-open-whiteboard.m4v");
const knowHeaderVideo = getTutorialVideoUrl("02-create-or-open-board.m4v");
const setUpPagesVideo = getTutorialVideoUrl("04-set-up-pages.m4v");
const workingViewVideo = getTutorialVideoUrl("05-working-view.m4v");
const loadSearchPlayersVideo = getTutorialVideoUrl("06-load-or-search-players.m4v");
const useAddPlayersVideo = getTutorialVideoUrl("08-drag-one-player.m4v");
const headingsPresetsVideo = getTutorialVideoUrl("09-headings-and-presets.m4v");
const moveOrganizeCardsVideo = getTutorialVideoUrl("11-move-organize-cards.m4v");
const onPageVideo = getTutorialVideoUrl("10-on-page.m4v");
const nationalPillsVideo = getTutorialVideoUrl("11-national-pills.m4v");
const draftListVideo = getTutorialVideoUrl("12-draft-list.m4v");
const playerDataVideo = getTutorialVideoUrl("12-data-box.m4v");
const notesGradeVideo = getTutorialVideoUrl("14-notes-and-grade.m4v");
const finishUpVideo = getTutorialVideoUrl("finish-up.m4v");

const WHITEBOARD_TUTORIAL_STEPS = [
  {
    title: "Create Or Open A Board",
    imageKey: "create-open-board",
    media: {
      type: "video",
      src: createOpenBoardVideo,
    },
    summary: "On the welcome screen, either name a new board or open an existing saved/shared board.",
    points: [
      "New board names need at least 10 characters.",
      "Saved and shared boards can be opened without first creating a new local board.",
      "Boards save locally first, then sync to the database when the user is signed in.",
    ],
  },
  {
    title: "Know The Header",
    imageKey: "white-board-header",
    media: {
      type: "video",
      src: knowHeaderVideo,
    },
    summary: "The top bar holds the board name, board tools, print, and the main player-loading controls.",
    points: [
      "Use Boards to create, open, or save boards.",
      "Use Share to give Team Members access when you own the board.",
      "Use Add for headings, details, and other page items.",
      "The Saved status flashes after a successful save.",
    ],
  },
  {
    title: "Set Up Pages",
    imageKey: "pages",
    media: {
      type: "video",
      src: setUpPagesVideo,
    },
    summary: "A board can have multiple pages. Pages keep their own players, headings, notes, and presets.",
    points: [
      "Use Add Page to create another page.",
      "Pages can be renamed, deleted, or reordered using Edit Pages.",
    ],
  },
  {
    title: "Choose The Page Layout",
    imageKey: "views-guides",
    media: {
      type: "video",
      src: workingViewVideo,
    },
    summary: "The bottom-left View popout switches between print sizing and guide views.",
    points: [
      "Open the View popout to choose Print Size, Guide Right, or Guide Centered.",
      "Print Size shows the printable board size.",
      "Guide Right shows the print guide inset on the right side of the workspace.",
      "Guide Centered shows the print guide centered in the workspace.",
    ],
  },
  {
    title: "Load Or Search Players",
    imageKey: "load-search-players",
    media: {
      type: "video",
      src: loadSearchPlayersVideo,
    },
    summary: "Use L1 - Lists, L2 - Shared Lists, SQ - Saved Queries, or Players search to add players to the Players button.",
    points: [
      "Players loaded before opening White Board will still be available when White Board opens.",
      "Use Add Players to open the Players button list and drag players onto the page.",
    ],
  },
  {
    title: "Use Add Players",
    imageKey: "add-players-popup",
    media: {
      type: "video",
      src: useAddPlayersVideo,
    },
    summary: "Add Players opens the source list used to place players onto the current page.",
    points: [
      "Filter by last name, first name, general position, or unit.",
      "Used shows players already on the board.",
      "Add All to Page adds every visible unused player to the current page.",
    ],
  },
  {
    title: "Add Headings And Presets",
    imageKey: "headings-presets",
    media: {
      type: "video",
      src: headingsPresetsVideo,
    },
    summary: "Use headings or saved presets to create structure before placing players.",
    points: [
      "Add Heading creates a movable heading.",
      "Preset layouts can add common heading groups quickly.",
      "Moved headings snap vertically to nearby heading rows for cleaner alignment.",
    ],
  },
  {
    title: "Auto-Place Under Headings",
    imageKey: "auto-place-headings",
    media: {
      type: "video",
      src: headingsPresetsVideo,
    },
    summary: "When headings are available, Add All to Page places matching players under matching headings.",
    points: [
      "CB, DB, DC, S, and FS can match related defensive headings.",
      "OL can distribute under LT, LG, C, RG, and RT headings.",
      "Unmatched players go to the fallback area at the bottom of the page.",
    ],
  },
  {
    title: "Move And Organize Cards",
    imageKey: "move-organize",
    media: {
      type: "video",
      src: moveOrganizeCardsVideo,
    },
    summary: "Drag cards around the board to refine the layout.",
    points: [
      "Cards can be dragged from one heading area to another.",
      "Dropping a player under a heading stacks that heading group neatly.",
      "Use Group and Select All to move multiple items together.",
    ],
  },
  {
    title: "Use Placed Players",
    imageKey: "on-page-popup",
    media: {
      type: "video",
      src: onPageVideo,
    },
    summary: "Placed Players shows the players currently placed on the active page.",
    points: [
      "Filter Placed Players by name or position.",
      "Drag from Placed Players back onto the board to create another player instance.",
      "The counter shows how many times that player appears on the page.",
      "Selecting a player in Placed Players highlights that player on the board.",
    ],
  },
  {
    title: "Use National Pills",
    imageKey: "national-pills",
    media: {
      type: "video",
      src: nationalPillsVideo,
    },
    summary: "Use the colored pills to filter which players are shown on the board.",
    points: [
      "The pills filter the players on the board by player category.",
      "The toggle switches the counts between the current Page and the full Board.",
      "Use Edit Pages to choose whether each page is included in the Board count.",
      "When a player is on more than one page, selecting that player highlights the matching board card yellow.",
      "Use the highlights to quickly find every place that player appears.",
    ],
  },
  {
    title: "Build The Priority List",
    imageKey: "draft-list",
    media: {
      type: "video",
      src: draftListVideo,
    },
    summary: "Priority is a board-wide list that can be built from players on the board.",
    points: [
      "Drag players into Priority or use Add All to Priority from Placed Players.",
      "A player can only appear once in Priority.",
      "Drag Priority rows to reorder them, or use X to remove a mistaken entry.",
    ],
  },
  {
    title: "Open Player Data",
    imageKey: "data-notes",
    media: {
      type: "video",
      src: playerDataVideo,
    },
    summary: "Select a player to view more information in Player Data.",
    points: [
      "Selected board cards turn yellow.",
      "Data shows measurements plus CFL, NFL, UFL, and Event information.",
      "This popup contains the proprietary White Board note, grade, position, and number.",
    ],
  },
  {
    title: "Personalize The Player",
    imageKey: "personalize-player",
    media: {
      type: "video",
      src: notesGradeVideo,
    },
    summary: "Add team-wide details that follow the player on White Board.",
    points: [
      "Add a team-wide grade, position, number, and notes.",
      "These details are team-wide, so anyone from your team who adds that player to a board will see them.",
      "A player card shows an underline when it has proprietary White Board information, including notes, position, or number.",
    ],
  },
  {
    title: "Clean Up And Finish",
    imageKey: "finish-board",
    media: {
      type: "video",
      src: finishUpVideo,
    },
    summary: "Use the left-side board tools to clean up, undo, save, and print.",
    points: [
      "Open the Clear popout and choose Players to remove players while keeping headings and notes.",
      "Open the Clear popout and choose Complete Board to clear the current page.",
      "Undo reverses the last board change when available.",
      "Print uses the current board page layout.",
    ],
  },
];

const WhiteBoardTutorial = ({ onClose }) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStep = WHITEBOARD_TUTORIAL_STEPS[activeStepIndex];
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === WHITEBOARD_TUTORIAL_STEPS.length - 1;

  const goToPreviousStep = () => {
    setActiveStepIndex((index) => Math.max(0, index - 1));
  };

  const goToNextStep = () => {
    setActiveStepIndex((index) => Math.min(WHITEBOARD_TUTORIAL_STEPS.length - 1, index + 1));
  };

  return (
    <div className="white-board-tutorial-backdrop" role="presentation">
      <div className="white-board-tutorial" role="dialog" aria-modal="true" aria-labelledby="white-board-tutorial-title">
        <div className="white-board-tutorial-header">
          <div>
            <div className="white-board-tutorial-kicker">White Board Tutorial</div>
            <div id="white-board-tutorial-title" className="white-board-tutorial-title">
              {activeStep.title}
            </div>
          </div>
          <button type="button" className="white-board-tutorial-close" onClick={onClose} aria-label="Close tutorial">
            X
          </button>
        </div>

        <div className="white-board-tutorial-body">
          <div className="white-board-tutorial-step-list" aria-label="Tutorial steps">
            {WHITEBOARD_TUTORIAL_STEPS.map((step, index) => (
              <button
                type="button"
                key={step.imageKey}
                className={`white-board-tutorial-step-button ${index === activeStepIndex ? "white-board-tutorial-step-button-active" : ""}`}
                onClick={() => setActiveStepIndex(index)}
              >
                <span className="white-board-tutorial-step-number">{index + 1}</span>
                <span className="white-board-tutorial-step-label">{step.title}</span>
              </button>
            ))}
          </div>

          <div className="white-board-tutorial-content">
            <div className="white-board-tutorial-image-slot" data-image-key={activeStep.imageKey}>
              {activeStep.media?.type === "video" ? (
                <video
                  key={activeStep.media.src}
                  className="white-board-tutorial-video"
                  controls
                  muted
                  playsInline
                  preload="metadata"
                >
                  <source src={activeStep.media.src} type="video/mp4" />
                </video>
              ) : (
                <>
                  <div className="white-board-tutorial-image-label">Image placeholder</div>
                  <div className="white-board-tutorial-image-key">{activeStep.imageKey}</div>
                </>
              )}
            </div>

            <div className="white-board-tutorial-copy">
              <div className="white-board-tutorial-progress">
                Step {activeStepIndex + 1} of {WHITEBOARD_TUTORIAL_STEPS.length}
              </div>
              <p>{activeStep.summary}</p>
              <ul>
                {activeStep.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="white-board-tutorial-footer">
          <button type="button" className="white-board-tutorial-nav-button" onClick={goToPreviousStep} disabled={isFirstStep}>
            Previous
          </button>
          <button type="button" className="white-board-tutorial-nav-button white-board-tutorial-nav-button-primary" onClick={isLastStep ? onClose : goToNextStep}>
            {isLastStep ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhiteBoardTutorial;
